import { useState, type FormEvent, type ReactElement } from 'react';
import { Link } from '@tanstack/react-router';
import {
  CoachBadRequestError,
  CoachBudgetExceededError,
  CoachInvalidPlanError,
  CoachNotConfiguredError,
  CoachUpstreamError,
  CreateSeasonPlanBodySchema,
  EVENT_TYPES,
  eventTypeLabel,
  useCreateSeasonPlan,
  type CreateSeasonPlanBody,
  type CreateSeasonPlanResponse,
  type EventType,
} from '@/lib/season';

/**
 * Formulario para crear un nuevo macrociclo / plan de temporada (M2.2).
 *
 * Decisiones:
 * - Validación cliente con el mismo `CreateSeasonPlanBodySchema` que usa el
 *   backend (Zod). Además añadimos una regla extra: la fecha del evento debe
 *   ser FUTURA (>= mañana). El backend lo valida también, pero capturarlo
 *   client-side evita una llamada a Anthropic carísima si el usuario tipea
 *   una fecha pasada.
 * - Default `weeklyHoursBaseline = 10` según contrato.
 * - Mientras `isPending`, mostramos banner ámbar avisando que el coach puede
 *   tardar 10-30s (es la llamada con tool-use más larga de la app — genera
 *   ~6-30 fases en una sola respuesta).
 * - Errores: reutilizamos las clases de error del coach. Para 503
 *   `anthropic_no_configurado` añadimos un `<Link>` accionable a
 *   `/ajustes/anthropic` además del mensaje.
 */
export function NewSeasonForm({
  onResult,
}: {
  onResult?: (result: CreateSeasonPlanResponse) => void;
}): ReactElement {
  const mutation = useCreateSeasonPlan();

  const [name, setName] = useState<string>('');
  const [eventType, setEventType] = useState<EventType>('70.3');
  const [eventDate, setEventDate] = useState<string>('');
  const [goalDescription, setGoalDescription] = useState<string>('');
  const [weeklyHoursBaseline, setWeeklyHoursBaseline] = useState<number>(10);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  function buildBody(): CreateSeasonPlanBody {
    // El input `type="date"` da YYYY-MM-DD. El contrato del backend acepta el
    // ISO completo o el corto; le mandamos YYYY-MM-DD que es lo más cercano
    // al schema (`event_date_iso` en BD).
    const body: CreateSeasonPlanBody = {
      name: name.trim(),
      eventType,
      eventDateISO: eventDate,
      weeklyHoursBaseline,
    };
    const goal = goalDescription.trim();
    if (goal) body.goalDescription = goal;
    return body;
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (mutation.isPending) return;

    const errors: Record<string, string> = {};

    // Validación adicional: la fecha debe ser futura (>= mañana en UTC).
    // Comparamos como timestamp UTC para no tener sesgos de DST.
    if (eventDate) {
      const evtT = Date.parse(`${eventDate}T00:00:00Z`);
      const todayUTC = new Date();
      const todayMidnightUTC = Date.UTC(
        todayUTC.getUTCFullYear(),
        todayUTC.getUTCMonth(),
        todayUTC.getUTCDate(),
      );
      if (!Number.isFinite(evtT) || evtT <= todayMidnightUTC) {
        errors.eventDateISO = 'La fecha del evento debe ser futura.';
      }
    }

    const candidate = buildBody();
    const parsed = CreateSeasonPlanBodySchema.safeParse(candidate);
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0] ?? '');
        if (key && !errors[key]) errors[key] = issue.message;
      }
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    mutation.mutate(parsed.data!, {
      onSuccess: (result) => onResult?.(result),
    });
  }

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      className="space-y-6"
      aria-labelledby="season-form-title"
    >
      <header>
        <h1
          id="season-form-title"
          className="text-2xl font-semibold tracking-tight text-slate-900"
        >
          Crear plan de temporada
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          Define tu evento objetivo y el coach planificará las fases BASE →
          BUILD → PEAK → TAPER hasta esa fecha. Puede tardar 10-30 segundos.
        </p>
      </header>

      <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          Nombre del plan
        </h2>
        <label htmlFor="season-name" className="sr-only">
          Nombre del plan
        </label>
        <input
          id="season-name"
          type="text"
          value={name}
          maxLength={200}
          onChange={(e) => setName(e.target.value)}
          placeholder="Ej: Camino a IM Lanzarote 2027"
          aria-invalid={fieldErrors.name !== undefined}
          aria-describedby={fieldErrors.name ? 'season-name-error' : undefined}
          className="mt-3 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
        />
        {fieldErrors.name && (
          <p id="season-name-error" role="alert" className="mt-1 text-xs text-rose-700">
            {fieldErrors.name}
          </p>
        )}
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          Evento
        </h2>
        <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label
              htmlFor="season-event-type"
              className="block text-sm font-medium text-slate-700"
            >
              Tipo de evento
            </label>
            <select
              id="season-event-type"
              value={eventType}
              onChange={(e) => setEventType(e.target.value as EventType)}
              aria-invalid={fieldErrors.eventType !== undefined}
              className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
            >
              {EVENT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {eventTypeLabel(t)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label
              htmlFor="season-event-date"
              className="block text-sm font-medium text-slate-700"
            >
              Fecha del evento
            </label>
            <input
              id="season-event-date"
              type="date"
              value={eventDate}
              onChange={(e) => setEventDate(e.target.value)}
              aria-invalid={fieldErrors.eventDateISO !== undefined}
              aria-describedby={
                fieldErrors.eventDateISO ? 'season-event-date-error' : undefined
              }
              className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
            />
            {fieldErrors.eventDateISO && (
              <p
                id="season-event-date-error"
                role="alert"
                className="mt-1 text-xs text-rose-700"
              >
                {fieldErrors.eventDateISO}
              </p>
            )}
          </div>
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          Objetivo cualitativo (opcional)
        </h2>
        <label htmlFor="season-goal" className="sr-only">
          Objetivo cualitativo
        </label>
        <textarea
          id="season-goal"
          rows={3}
          maxLength={2000}
          value={goalDescription}
          onChange={(e) => setGoalDescription(e.target.value)}
          placeholder="Ej: terminar bajo 12h, primer Ironman, mejorar T2 del año pasado."
          aria-invalid={fieldErrors.goalDescription !== undefined}
          className="mt-3 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
        />
        <p className="mt-1 text-xs text-slate-500">
          {goalDescription.length} / 2000 caracteres
        </p>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          Horas semanales base
        </h2>
        <label
          htmlFor="season-hours"
          className="mt-3 block text-sm font-medium text-slate-700"
        >
          Horas/semana de referencia (1-30)
        </label>
        <input
          id="season-hours"
          type="number"
          min={1}
          max={30}
          step={1}
          value={weeklyHoursBaseline}
          onChange={(e) =>
            setWeeklyHoursBaseline(clampHoursBaseline(e.target.value))
          }
          aria-invalid={fieldErrors.weeklyHoursBaseline !== undefined}
          aria-describedby={
            fieldErrors.weeklyHoursBaseline ? 'season-hours-error' : undefined
          }
          className="mt-1 block w-32 rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
        />
        <p className="mt-1 text-xs text-slate-500">
          El coach calculará BASE/BUILD/PEAK/TAPER a partir de este baseline
          (peak = +15%, taper = ~70%, race week = ~50%).
        </p>
        {fieldErrors.weeklyHoursBaseline && (
          <p id="season-hours-error" role="alert" className="mt-1 text-xs text-rose-700">
            {fieldErrors.weeklyHoursBaseline}
          </p>
        )}
      </section>

      {mutation.isPending && (
        <div
          role="status"
          aria-live="polite"
          aria-busy="true"
          className="flex items-center gap-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900"
        >
          <Spinner />
          <span>
            El coach está diseñando tu macrociclo…{' '}
            <span className="text-xs text-amber-800">
              (esto puede tardar entre 10 y 30 segundos)
            </span>
          </span>
        </div>
      )}

      {mutation.isError && <ErrorBanner error={mutation.error} />}

      <div className="flex items-center justify-end gap-3">
        <button
          type="submit"
          disabled={mutation.isPending}
          aria-busy={mutation.isPending}
          className="rounded-md bg-teal-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {mutation.isPending ? (
            <span className="inline-flex items-center gap-2">
              <Spinner /> Generando plan de temporada…
            </span>
          ) : (
            'Crear plan de temporada'
          )}
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Error banner
// ---------------------------------------------------------------------------

function ErrorBanner({ error }: { error: Error }): ReactElement {
  if (error instanceof CoachNotConfiguredError) {
    return (
      <div
        role="alert"
        className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800"
      >
        Falta configurar tu ANTHROPIC_API_KEY.{' '}
        <Link
          to="/ajustes/anthropic"
          className="font-semibold text-rose-900 underline hover:text-rose-700"
        >
          Configúrala en /ajustes/anthropic
        </Link>
        .{error.hint ? ` ${error.hint}` : ''}
      </div>
    );
  }
  if (error instanceof CoachBudgetExceededError) {
    return (
      <div
        role="alert"
        className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800"
      >
        {error.hint ||
          'Has alcanzado tu presupuesto mensual de Anthropic. Súbelo en /ajustes/anthropic o espera al mes siguiente.'}
      </div>
    );
  }
  if (error instanceof CoachUpstreamError) {
    return (
      <div
        role="alert"
        className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800"
      >
        Error de Anthropic, vuelve a intentar en un momento.
      </div>
    );
  }
  if (error instanceof CoachInvalidPlanError) {
    return (
      <div
        role="alert"
        className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800"
      >
        El coach generó un plan que no respeta las reglas. Detalle:{' '}
        {error.detail || 'sin detalle disponible'}
      </div>
    );
  }
  if (error instanceof CoachBadRequestError) {
    return (
      <div
        role="alert"
        className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800"
      >
        {error.issues.length > 0
          ? `Petición inválida: ${error.issues.join(' · ')}`
          : 'Petición inválida. Revisa los campos del formulario.'}
      </div>
    );
  }
  return (
    <div
      role="alert"
      className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800"
    >
      No se pudo crear el plan de temporada. {error.message ?? ''}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Spinner (inline)
// ---------------------------------------------------------------------------

function Spinner(): ReactElement {
  return (
    <span
      aria-hidden="true"
      className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent"
    />
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function clampHoursBaseline(raw: string): number {
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 1) return 1;
  if (n > 30) return 30;
  return Math.round(n);
}
